import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Search, BookOpen, ArrowRight, Tag, Eye } from "lucide-react";
import { Input } from "../components/ui/input";

export default function Blog() {
    const [params, setParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [cats, setCats] = useState([]);
    const [q, setQ] = useState(params.get("q") || "");
    const activeCat = params.get("category") || "";

    useEffect(() => {
        api.get("/blog/categories").then(r => setCats(r.data)).catch(() => { });
    }, []);

    useEffect(() => {
        const p = { limit: 30 };
        if (activeCat) p.category = activeCat;
        if (q) p.q = q;
        api.get("/blog", { params: p }).then(r => {
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
        });
    }, [activeCat, q]);

    const setCat = (c) => {
        const np = new URLSearchParams(params);
        if (c) np.set("category", c); else np.delete("category");
        setParams(np);
    };

    return (
        <div className="bg-stone-50 min-h-screen">
            <header className="bg-stone-900 text-stone-50 py-16 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="label-eyebrow text-sky-300">Hahnemann Journal</div>
                    <h1 className="font-serif text-5xl sm:text-6xl mt-2">Homeopathy notes &amp; education</h1>
                    <p className="text-stone-300 text-sm sm:text-base mt-3 max-w-xl">Remedy guides, case studies, and practical wellness reads — written by our practitioners.</p>
                    <div className="relative mt-6 max-w-xl">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="pl-9 h-11 rounded-none bg-stone-50 border-0 text-stone-900" data-testid="blog-search" />
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 py-10">
                {cats.length > 0 && (
                    <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="blog-categories">
                        <button onClick={() => setCat("")} className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${!activeCat ? "border-sky-700 bg-sky-700 text-stone-50" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`} data-testid="cat-all">All</button>
                        {cats.map(c => (
                            <button key={c.name} onClick={() => setCat(c.name)} className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${activeCat === c.name ? "border-sky-700 bg-sky-700 text-stone-50" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`} data-testid={`cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                {c.name} <span className="opacity-60 ml-1">({c.count})</span>
                            </button>
                        ))}
                    </div>
                )}

                {rows.length === 0 ? (
                    <div className="bg-white border border-stone-200 p-16 text-center" data-testid="blog-empty">
                        <BookOpen className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                        <p className="text-sm text-stone-500">{q ? "Nothing matches your search yet." : "No articles published yet. Check back soon."}</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-6">
                        {rows.map((p) => (
                            <Link to={`/blog/${p.slug}`} key={p.id} className="bg-white border border-stone-200 hover:border-sky-300 hover:shadow-sm transition-all group" data-testid={`blog-card-${p.slug}`}>
                                {p.cover_url && (
                                    <div className="aspect-[16/9] bg-stone-100 overflow-hidden">
                                        <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    </div>
                                )}
                                <div className="p-5">
                                    {p.category && <div className="text-[10px] uppercase tracking-widest text-sky-700 mb-2">{p.category}</div>}
                                    <h2 className="font-serif text-2xl text-stone-900 group-hover:text-sky-700 leading-snug">{p.title}</h2>
                                    {p.excerpt && <p className="text-sm text-stone-600 mt-2 line-clamp-3">{p.excerpt}</p>}
                                    <div className="flex items-center gap-3 mt-4 text-[11px] text-stone-500">
                                        <span>{p.author_name}</span>
                                        <span>·</span>
                                        <span>{(p.published_at || "").slice(0, 10)}</span>
                                        {p.views > 0 && <><span>·</span><span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{p.views}</span></>}
                                        <ArrowRight className="w-3.5 h-3.5 ml-auto group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {total > rows.length && (
                    <div className="text-xs text-stone-500 text-center mt-6">Showing {rows.length} of {total}</div>
                )}
            </main>
        </div>
    );
}
